// Centralized API service for all API calls

const GEOLOCATION_API = 'https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation';
const LEAD_API = 'https://leadapi.crmbytes.com/api/Lead/AddLead';

/**
 * Fetch user's geolocation data
 * @returns {Promise<Object>} Geolocation data with success status
 */
export const fetchGeolocation = async () => {
  try {
    const response = await fetch(GEOLOCATION_API, {
      method: 'GET',
      headers: {
        'accept': 'text/plain'
      }
    });
    
    const data = await response.json();
    
    if (data.isSuccess && data.statusCode === 200) {
      return {
        success: true,
        data: data.data,
        fullResponse: data // Store complete response for geoLocation field
      };
    }
    
    return {
      success: false,
      error: data.errorMessage || 'Failed to fetch location'
    };
  } catch (error) {
    console.error('Geolocation API Error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Submit lead form data to CRM
 * @param {Object} formData - Form data to submit
 * @param {Object} geoData - Geolocation response (optional)
 * @returns {Promise<Object>} API response with success/error details
 */
export const submitLead = async (formData, geoData = null) => {
  try {
    // Extract only numeric phone number (remove all non-digit characters)
    const cleanPhoneNumber = (formData.phoneNumber || '').replace(/\D/g, '');
    
    // If geoData is not available or ipAddress is missing, return error
    if (!geoData || !geoData.data || !geoData.data.ipAddress) {
      console.error('GeoData missing or invalid:', geoData);
      return {
        success: false,
        error: 'Unable to fetch your location. Please refresh and try again.'
      };
    }
    
    // Prepare payload according to API specification
    const payload = {
      name: formData.name,
      email: formData.email,
      countryDialCode: formData.countryDialCode || 1,
      country: formData.country || 'US',
      phoneNumber: cleanPhoneNumber, // ONLY numeric, no country code prefix
      ipAddress: geoData.data.ipAddress, // REQUIRED field
      description: formData.description || '',
      pageUrl: window.location.href,
      city: geoData.data.cityName || '',
      state: geoData.data.regionName || '',
      geoLocation: geoData.fullResponse ? JSON.stringify(geoData.fullResponse) : '',
      packageTitle: formData.packageTitle || '',
      packageAmount: formData.packageAmount || 0
    };

    console.log('Submitting lead with payload:', payload);

    const response = await fetch(LEAD_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    // Parse response JSON
    let data;
    try {
      data = await response.json();
      console.log('Lead API response:', data);
    } catch (jsonError) {
      console.error('Error parsing JSON response:', jsonError);
      throw new Error('Failed to parse API response');
    }

    // Handle 200 Success - Redirect to application page
    if (response.status === 200 && data.isSuccess && data.data?.customerId) {
      console.log('✅ SUCCESS - Redirecting with customerId:', data.data.customerId);
      return {
        success: true,
        customerId: data.data.customerId,
        message: data.data.message || 'Lead created successfully',
        redirectUrl: `/application/main?customerid=${data.data.customerId}`
      };
    }
    
    // If status is 200 but conditions not met, log the issue
    if (response.status === 200) {
      console.warn('⚠️ Status 200 but missing expected data:', {
        isSuccess: data.isSuccess,
        hasCustomerId: !!data.data?.customerId,
        fullResponse: data
      });
    } 
    
    // Handle 401 Unauthorized - Show message from API
    if (response.status === 401) {
      return {
        success: false,
        error: data.message || 'Unauthorized access',
        statusCode: 401
      };
    } 
    
    // Handle 400 Bad Request - Show errorMessage from API
    if (response.status === 400) {
      return {
        success: false,
        error: data.errorMessage || data.data?.message || 'Invalid request',
        statusCode: 400
      };
    }
    
    // Handle other errors
    return {
      success: false,
      error: data.errorMessage || data.message || 'Something went wrong',
      statusCode: response.status
    };
    
  } catch (error) {
    console.error('Lead API Error:', error);
    return {
      success: false,
      error: error.message || 'Network error occurred. Please try again.'
    };
  }
};

/**
 * Get country dial code from country code
 * @param {string} countryCode - Two letter country code (e.g., 'US', 'GB')
 * @returns {number} Country dial code
 */
export const getCountryDialCode = (countryCode) => {
  const dialCodes = {
    'US': 1, 'CA': 1, 'GB': 44, 'AU': 61, 'IN': 91, 'PK': 92,
    'BD': 880, 'CN': 86, 'JP': 81, 'DE': 49, 'FR': 33, 'IT': 39,
    'ES': 34, 'MX': 52, 'BR': 55, 'ZA': 27, 'NG': 234, 'AE': 971,
    'SG': 65, 'NZ': 64, 'NL': 31, 'BE': 32, 'CH': 41, 'AT': 43,
    'SE': 46, 'NO': 47, 'DK': 45, 'FI': 358, 'PL': 48, 'CZ': 420,
    'GR': 30, 'PT': 351, 'IE': 353, 'RU': 7, 'TR': 90, 'SA': 966,
    'EG': 20, 'KE': 254, 'TH': 66, 'VN': 84, 'PH': 63, 'MY': 60,
    'ID': 62, 'KR': 82, 'TW': 886, 'HK': 852, 'IL': 972, 'AR': 54,
    'CL': 56, 'CO': 57, 'PE': 51, 'VE': 58
  };
  
  return dialCodes[countryCode?.toUpperCase()] || 1;
};

/**
 * Get country flag emoji from country code
 * @param {string} countryCode - Two letter country code
 * @returns {string} Country flag emoji
 */
export const getCountryFlag = (countryCode) => {
  if (!countryCode || countryCode.length !== 2) return '🌍';
  
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt());
  
  return String.fromCodePoint(...codePoints);
};
